// #2149 (docs/score/announcer-ui-redesign.md §4.3) — the live desk's pure
// selection logic. Three fixed slots (JUST FINISHED / IN THE RING NOW / NEXT
// UP) read off the SAME `rows` array every other band already gets from
// useRingData() — no new fetch, no new hook.
//
// Anchor logic (§4.3 "When nothing is running"): ring-state's classId/rideId
// are wiped to null on BREAK / END_OF_DAY (confirmed in
// apps/scoreadmin-fe/src/components/RingStateSection.jsx), so `currentRideId`
// stops resolving to a real row at exactly the moment the spec says slot 2
// must swap to a state line instead of inventing a ride. Two cases:
//
//   1. `currentRideId` resolves inside `rideRows` -> that row IS the ride
//      genuinely in the ring. JUST FINISHED = the prior row, NEXT UP = the
//      following N.
//   2. It does not resolve (null, or stale/not-found) -> there is nothing
//      genuinely in the ring right now. Anchor instead to the LAST ride whose
//      status is not PENDING (the most recently completed ride) so JUST
//      FINISHED / NEXT UP keep telling the truth (§4.3: "keep their real
//      contents") while the caller renders slot 2's break/done state line
//      instead of a ride.
import { ROW_KIND, RESULT_STATUS, buildClassResults, normalizeStatus } from "./orderOfGo.js";
import { RIDE_STATUSES } from "score-common";

// Returns `{ index, isCurrent }` for the row `rideRows[index]` to anchor the
// desk on, or null when there is no ride at all to anchor to (an empty ring).
// `isCurrent: true` means the anchor is genuinely the ride in the ring right
// now; `isCurrent: false` means it is being used as the JUST FINISHED
// fallback (ring on break / done for the day, rule above).
export function findLiveDeskAnchor(rideRows, currentRideId) {
  const rows = rideRows || [];
  if (rows.length === 0) return null;

  if (currentRideId) {
    const index = rows.findIndex((r) => r.id === currentRideId);
    if (index >= 0) return { index, isCurrent: true };
  }

  for (let i = rows.length - 1; i >= 0; i--) {
    if (normalizeStatus(rows[i].status) !== RIDE_STATUSES.PENDING) {
      return { index: i, isCurrent: false };
    }
  }
  return null;
}

// `{ currentRow, justFinishedRow, nextUpRows }` — the three slots' data,
// nextUpRows annotated with `differentClass` (§4.3: "a ride from a different
// class is marked with that class; the rest are assumed to be the current
// one"). The reference classId for that comparison is, in order: the
// current ride's class, else the just-finished ride's class, else the first
// NEXT UP row's own class (so the first row is never marked different from
// itself).
export function selectLiveDeskSequence({ rows, currentRideId, nextUpCount = 3 }) {
  const rideRows = (rows || []).filter((r) => r.kind === ROW_KIND.RIDE);
  const anchor = findLiveDeskAnchor(rideRows, currentRideId);
  if (!anchor) {
    return { currentRow: null, justFinishedRow: null, nextUpRows: [] };
  }

  const { index, isCurrent } = anchor;
  const currentRow = isCurrent ? rideRows[index] : null;
  const justFinishedRow = isCurrent
    ? index > 0
      ? rideRows[index - 1]
      : null
    : rideRows[index];
  const rawNextUp = rideRows.slice(index + 1, index + 1 + Math.max(0, nextUpCount));

  const referenceClassId = currentRow?.classId ?? justFinishedRow?.classId ?? rawNextUp[0]?.classId ?? null;
  const nextUpRows = rawNextUp.map((row) => ({
    ...row,
    differentClass: referenceClassId != null && row.classId !== referenceClassId,
  }));

  return { currentRow, justFinishedRow, nextUpRows };
}

// A thin wrapper over orderOfGo.js's buildClassResults, scoped to ONE ride
// row. `resultsByClassId` is RingDataContext's raw classId -> GET
// /class/:id/results snapshot (lifted from whichever ClassResults instance
// mounted that class, #2149's Slot 1 reads it PASSIVELY — see LiveDesk.jsx),
// not yet joined to a rideRows array; buildClassResults does that join.
export function findRideResult(resultsByClassId, rideRow) {
  if (!rideRow?.classId) return null;
  const raw = resultsByClassId?.[rideRow.classId];
  if (!raw || raw.length === 0) return null;
  const built = buildClassResults(raw, [rideRow]);
  return built.find((r) => r.rideId === rideRow.id) || null;
}

// Slot 1 shows a full-height "score pending" placeholder until the result is
// revealed (CONFIRMED/FINAL), never a half-computed score (Rule 3).
export function isRideResultPending(result) {
  if (!result) return true;
  return result.status !== RESULT_STATUS.CONFIRMED && result.status !== RESULT_STATUS.FINAL;
}

export const JUST_FINISHED_MODE = {
  PENDING: "PENDING",
  CONFIRMED: "CONFIRMED",
  FINAL: "FINAL",
};

// AC10 — CONFIRMED renders a checkmark + the literal word "Confirmed" (never
// colour alone); FINAL renders the placing text. Kept as pure logic so the
// mode decision (and AC12's placing===0 case below) is unit-testable
// independent of resultsByClassId's real data source, which stays empty on
// this card's shipped surface until #2141/#2142 mount a look-up column.
export function describeJustFinishedMode(result) {
  if (!result) return JUST_FINISHED_MODE.PENDING;
  if (result.status === RESULT_STATUS.CONFIRMED) return JUST_FINISHED_MODE.CONFIRMED;
  if (result.status === RESULT_STATUS.FINAL) return JUST_FINISHED_MODE.FINAL;
  return JUST_FINISHED_MODE.PENDING;
}

// AC12 — `result.placing === 0` is STORED DATA meaning UNPLACED, not an
// index to add 1 to (reference_placing_zero_is_stored_data_not_index).
// Returns `{ unplaced: true }` for 0/null/undefined, `{ unplaced: false,
// placing }` for any genuine positive placing. Only meaningful once
// describeJustFinishedMode(result) === FINAL.
export function describeJustFinishedPlacing(result) {
  if (result?.placing != null && result.placing > 0) {
    return { unplaced: false, placing: result.placing };
  }
  return { unplaced: true, placing: null };
}
